import type { PairKind } from './pair-types.js';

/**
 * Emotes: a player has a hero make a short gesture that everyone in the room sees. They are talk, not rules: the server only checks that the hero is
 * free (`emote-system.ts`) and stamps the hero's `emote`, and the browsers pose it. Solo emotes are Blender clips shared by both bodies
 * (`emote-<kind>`, `docs/animation-authoring.md#one-character-clips`); the pair emotes are the existing meeting clips.
 */
export const EMOTE_SECONDS = { wave: 2.6, cheer: 2.8, point: 2.4, bow: 2.4 } as const;
export type EmoteKind = keyof typeof EMOTE_SECONDS;
export const EMOTE_KINDS = Object.keys(EMOTE_SECONDS) as EmoteKind[];
export const isEmoteKind = (value: unknown): value is EmoteKind => typeof value === 'string' && Object.hasOwn(EMOTE_SECONDS, value);
/** Emotes made with an ally: the hero walks up to it and the meeting plays (`pairing-system.ts`). */
export const PAIR_EMOTES = ['handshake', 'hug'] as const satisfies readonly PairKind[];
export type PairEmoteKind = typeof PAIR_EMOTES[number];
export const isPairEmote = (value: unknown): value is PairEmoteKind => typeof value === 'string' && (PAIR_EMOTES as readonly string[]).includes(value);
/** The solo emote's clip in `src/content/solo-animations`. */
export const emoteClipId = (kind: EmoteKind) => `emote-${kind}`;
/** At most this many emotes per player in any window of `EMOTE_WINDOW_MS`; the rest are refused with a short reason. */
export const EMOTE_BURST = 3;
export const EMOTE_WINDOW_MS = 2000;

/** A hero's emote under way, on the game clock. `facing` is the way a pointing hero turns (radians; forward is (sin, cos) on the ground). */
export interface EmoteState { kind: EmoteKind; startedAt: number; facing?: number }
